import Highcharts from "highcharts";
// hasSideEffects in the file name is important, see package.json and https://webpack.js.org/guides/tree-shaking
import "./fix/pattern-fill.hasSideEffects";
import "highcharts/es-modules/Series/AreaRange/AreaRangeSeries";
import "highcharts/es-modules/Series/Bubble/BubbleSeries";

import betterZoom from "./plugins/betterZoom";
import keydownChartEvent from "./plugins/keydownChartEvent";
import mousemoveChartEvent from "./plugins/mousemoveChartEvent";
import pointClickChartEvent from "./plugins/pointClickChartEvent";
import stickyTooltips from "./plugins/stickyTooltip";
import tooltipPosition from "./plugins/tooltipPosition";
import { easingSoftMs, easingSoftJS } from "..";
import fixScrollablePlotArea from "./fix/fixScrollablePlotArea";
import tooltipClick from "./plugins/tooltipClick";
import improveDrawPoints from "./plugins/improveDrawPoints";
import fixCSPIssues from "./fix/fixCspIssues";
import formatDate from "../i18n/formatDate";
import { color } from "../index";
import forceStartEndTicks from "./plugins/forceStartEndTicks";
import fixNavigatorSeriesUpdate from "./fix/fixNavigatorSeriesUpdate";
import zoomReset from "./plugins/zoomReset";
import findPointIndex from "./plugins/findPointIndex";
import formatNumber from "../i18n/formatNumber";
import legendItemHoverCallback from "./plugins/legendItemHoverCallback";
import fixNavigatorMouseMove from "./fix/fixNavigatorMouseMove";
import betterAxisTick from "./plugins/betterAxisTick";
import fixPlotlinesZIndex from "./fix/fixPlotlinesZIndex";
import handleAxisInitialUserMinMax from "./plugins/handleAxisInitialUserMinMax";
import deferSeriesRender from "./plugins/deferSeriesRender";
import fixGetTimeTicksWhenStartEqualEnd from "./fix/fixGetTimeTicksWhenStartEqualEnd";
import fixNavigatorRedraw from "./fix/fixNavigatorRedraw";
import fixNavigatorColumnSerieThreshold from "./fix/fixNavigatorColumnSerieThreshold";
import fixGetPointsCollection from "./fix/fixGetPointsCollection";
import fixOnSeriesEvents from "./fix/fixOnSeriesEvents";
import fixAxisVal2lin from "./fix/fixAxisVal2lin";

const CHART_ANIMATION_DURATION_MS = easingSoftMs;
const CHART_ANIMATION_EASING = easingSoftJS;

const AXIS_LABEL_COLOR = color.carbon600;
export const DEFAULT_MARGIN_RIGHT_PX = 20;

const DATETIME_DISPLAY_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

// Display time in labels only when zoomed in (< 7 days)
// and at least two tick labels fall on the same day
function shouldShowDateTime(chart: Highcharts.Chart): boolean {
  const xAxis = chart.xAxis[0];
  if (
    !xAxis ||
    typeof xAxis.min !== "number" ||
    typeof xAxis.max !== "number" ||
    !Array.isArray(xAxis.tickPositions)
  ) {
    return false;
  }

  const visibleRange = xAxis.max - xAxis.min;
  if (visibleRange > DATETIME_DISPLAY_THRESHOLD_MS) return false;

  const seenDates = new Set<string>();

  for (const tick of xAxis.tickPositions) {
    const d = new Date(tick);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (seenDates.has(key)) return true;
    seenDates.add(key);
  }

  return false;
}

export function getTimeFormat(chart: Highcharts.Chart) {
  return shouldShowDateTime(chart) ? "datetime" : "date";
}

export const zIndexChart = {
  areaSeries: 2000,
  plotBands: 3000,
  plotLines: 4000,
  lineSeries: 5000,
  flagSeries: 6000,
} as const;

declare module "highcharts" {
  interface SeriesOptions {
    // replaces completely showInNavigator with our implementation
    replicateInNavigator: boolean;
  }

  interface XAxisPlotLinesLabelOptions {
    className?: string; // missing
  }

  interface PlotLineOrBand {
    id: string;
    svgElem: SVGElement;
  }

  interface PlotLineOptions {
    from?: number;
    value?: number;
  }

  interface XAxisPlotBandsLabelOptions {
    className?: string; // missing
  }

  interface Axis {
    zoomEnabled: boolean;
    tickInterval: number;
    userMin?: number | null;
    userMax?: number | null;
    plotLinesAndBands?: PlotLineOrBand[];
  }

  interface XAxisOptions {
    disableAnimation?: boolean;
  }

  interface XAxisLabelsOptions {
    onClick?: (event: MouseEvent, label: { value: string }) => void;
  }

  interface YAxisOptions {
    initialUserMin?: number | null;
    initialUserMax?: number | null;
  }

  interface ZAxisOptions {
    initialUserMin?: number | null;
    initialUserMax?: number | null;
    breaks: XAxisBreaksOptions[] | undefined;
  }

  interface YAxisLabelsOptions {
    unit?: string;
    sign?: boolean;
    noSpaceBetweenValueAndUnit?: boolean;
    precision?: number;
  }

  interface Point {
    id: string; // missing (present in PointOptionsObject)
    description?: string;
  }
  interface PointOptionsObject {
    description?: string;
  }

  interface Series {
    legendSymbol: Highcharts.SVGElement;
    legendItem: Highcharts.SVGElement;
  }

  interface Chart {
    name: string;
    timezone: string | null;
  }

  interface NavigatorYAxisOptions {
    scrollbar?: {
      enabled?: boolean;
    };
  }
}

fixCSPIssues(Highcharts);
fixNavigatorColumnSerieThreshold(Highcharts);
forceStartEndTicks(Highcharts);
betterZoom(Highcharts);
zoomReset(Highcharts);
mousemoveChartEvent(Highcharts);
keydownChartEvent(Highcharts);
pointClickChartEvent(Highcharts);
stickyTooltips(Highcharts);
tooltipClick(Highcharts);
tooltipPosition(Highcharts);
fixScrollablePlotArea(Highcharts);
improveDrawPoints(Highcharts);
fixNavigatorSeriesUpdate(Highcharts);
fixNavigatorMouseMove(Highcharts);
legendItemHoverCallback(Highcharts);
findPointIndex(Highcharts);
betterAxisTick(Highcharts);
fixPlotlinesZIndex(Highcharts);
handleAxisInitialUserMinMax(Highcharts);
deferSeriesRender(Highcharts);
fixGetTimeTicksWhenStartEqualEnd(Highcharts);
fixNavigatorRedraw(Highcharts);
fixGetPointsCollection(Highcharts);
fixOnSeriesEvents(Highcharts);
fixAxisVal2lin(Highcharts);

Highcharts.setOptions({
  deferSeriesRender: true,
  credits: { enabled: false },
  legend: {
    enabled: false,
  },
  chart: {
    reflow: false,
    style: {
      fontFamily: "Open Sans",
      fontWeight: "bold",
    },
    animation: {
      duration: CHART_ANIMATION_DURATION_MS,
      easing: CHART_ANIMATION_EASING,
    },
    panning: { enabled: true, type: "xy" },
    panKey: "meta",
    spacing: [0, 0, 0, 0],
    marginRight: DEFAULT_MARGIN_RIGHT_PX, // removes auto-margin, set back to undefined to get auto-margin when there is a yAxis on the right
    fitWrapperWidth: true,
    fitWrapperHeight: true,
    ignoreHiddenSeries: true,
  },
  title: {
    text: undefined,
  },
  tooltip: {
    backgroundColor: color.hydrogen,
    valueDecimals: 2,
    borderRadius: 4,
    followPointer: false,
    distance: 25,
    useHTML: true,
    hideDelay: 0,
    outside: true, // use the same renderer everywhere
    style: {
      fontFamily: "Open Sans",
    },
    padding: 10,
    animation: false,
  },
  plotOptions: {
    series: {
      connectNulls: false,
      stickyTracking: false,
      showInNavigator: false, // needed, we do not want Highcharts behavior. Do not remove, it messes replicateInNavigator
      findNearestPointBy: "x",
      // not compatible with deferSeriesRender (applied just after chart render, before defered serie render)
      dataLabels: {
        enabled: false,
      },
      turboThreshold: 0,
      animation: false, // initial serie loading animation
    },
    line: {
      zIndex: zIndexChart.lineSeries,
      states: {
        select: {
          animation: false,
        },
        normal: {
          animation: false,
        },
        hover: {
          lineWidthPlus: 0,
          animation: false,
        },
        inactive: {
          opacity: 0.125,
          animation: false,
        },
      },
    },
    arearange: {
      zIndex: zIndexChart.areaSeries,
      enableMouseTracking: false,
      marker: {
        enabled: false,
      },
      states: {
        select: {
          animation: false,
        },
        normal: {
          animation: false,
        },
        hover: {
          animation: false,
        },
        inactive: {
          animation: false,
        },
      },
    },
    flags: {
      zIndex: zIndexChart.flagSeries,
    },
  },
  xAxis: {
    maxPadding: 0, // remove small default padding
    minPadding: 0,
    minRange: 1,
    lineColor: color.titanium200,
    tickColor: color.titanium200,
    labels: {
      style: {
        color: AXIS_LABEL_COLOR,
        fontWeight: "600",
      },
      formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
        if (this.axis.userOptions.type === "datetime") {
          try {
            return formatDate(Number(this.value), getTimeFormat(this.chart), this.chart.timezone);
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            console.debug((error as any).message, {
              "this.value": this.value,
              error,
            });
            return "invalid date";
          }
        } else {
          return this.axis.defaultLabelFormatter.call(this);
        }
      },
    },
  },
  yAxis: {
    minPadding: 0,
    maxPadding: 0.08,
    resize: {
      enabled: true,
    },
    plotLines: [{ value: 0, width: 1, zIndex: zIndexChart.plotLines, color: color.titanium100 }],
    scrollbar: {
      enabled: true,
      showFull: false,
    },
    margin: 0,
    gridLineColor: color.titanium100,
    title: {
      text: " ",
      style: {
        color: color.carbon900,
        fontWeight: "600",
      },
    },
    labels: {
      style: {
        color: AXIS_LABEL_COLOR,
        fontWeight: "600",
      },
      formatter(this) {
        const options = (this.axis.userOptions as Highcharts.YAxisOptions).labels;
        if (options?.unit) {
          try {
            return formatNumber(Number(this.value), {
              sign: options.sign,
              unit: options.unit,
              noSpaceBetweenValueAndUnit: options.noSpaceBetweenValueAndUnit,
              precision: options.precision,
            });
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            console.debug((error as any).message, {
              "this.value": this.value,
              error,
            });
            return "";
          }
        } else {
          return this.axis.defaultLabelFormatter.call(this);
        }
      },
    },
  },
  navigator: {
    // if you plan to remove adaptToUpdatedData: false
    // test, test and test again (monkey click creating/modifying analyses changing charts like crazy)
    adaptToUpdatedData: false,
    series: {
      type: "line",
    },
    margin: 16,
    maskInside: true,
    maskFill: color.darkNitrogen500Fade080,
    outlineColor: color.titanium100,
    outlineWidth: 1,
    handles: {
      symbols: ["handle", "handle"],
      width: 10,
      height: 20,
      lineWidth: 0.3 as unknown as object,
      backgroundColor: color.hydrogen,
      borderColor: color.titanium600,
    },
    xAxis: {
      id: "navigator-x-axis",
      min: null,
      max: null,
      gridLineColor: color.titanium100,
      breaks: [],
      labels: {
        style: {
          color: AXIS_LABEL_COLOR,
          fontWeight: "600",
        },
        formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
          try {
            return formatDate(Number(this.value), "date", this.chart.timezone);
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            console.debug((error as any).message, {
              "this.value": this.value,
              error,
            });
            return "invalid date";
          }
        },
      },
    },
    yAxis: {
      id: "navigator-y-axis",
      scrollbar: {
        enabled: false,
      },
    },
  },
  scrollbar: {
    enabled: false,
    liveRedraw: true,
    margin: 5,
    height: 12,
  },
});

Highcharts.SVGRenderer.prototype.symbols["cross"] = function (
  x: number,
  y: number,
  w: number,
  h: number,
) {
  return [["M", x, y], ["L", x + w, y + h], ["M", x + w, y], ["L", x, y + h], ["z"]];
};

Highcharts.SVGRenderer.prototype.symbols["empty"] = function () {
  return [];
};

const CORNER_RADIUS = 4;
Highcharts.SVGRenderer.prototype.symbols["handle"] = function (
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const tlr = CORNER_RADIUS;
  const trr = CORNER_RADIUS;
  const brr = CORNER_RADIUS;
  const blr = CORNER_RADIUS;

  const lines = [
    ["M", x, y + tlr],
    ["A", tlr, tlr, 0, 0, 1, x + tlr, y],
    ["L", x + w - trr, y],
    ["A", trr, trr, 0, 0, 1, x + w, y + trr],
    ["L", x + w, y + h - brr],
    ["A", brr, brr, 0, 0, 1, x + w - brr, y + h],
    ["L", x + blr, y + h],
    ["A", blr, blr, 0, 0, 1, x, y + h - blr],
    ["Z"],
    ["M", x + w / 2 - 1.5, y + 6],
    ["L", x + w / 2 - 1.5, y + h - 6],
    ["M", x + w / 2 + 1.5, y + 6],
    ["L", x + w / 2 + 1.5, y + h - 6],
  ];
  const res = lines.reduce((acc, l) => acc.concat(l), []);
  return res;
};

Highcharts.SVGRenderer.prototype.symbols["rectangle"] = function (
  x: number,
  y: number,
  w: number,
  h: number,
) {
  return ["M", x, y, "H", x + w, "V", y + h / 2, "H", x, "Z"];
};

Highcharts.AST.allowedAttributes.push("data-action");
Highcharts.AST.allowedTags.push("g");
Highcharts.AST.allowedAttributes.push("title");
Highcharts.AST.allowedAttributes.push("data-name");
Highcharts.AST.allowedAttributes.push("viewbox");
Highcharts.AST.allowedAttributes.push("transform");

window.Highcharts = Highcharts;

export default Highcharts;
